import { Event, S3Object, WebLink } from "../../../models";
import {
    IonButton,
    IonButtons,
    IonCol,
    IonContent,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonRow,
    IonTitle,
    IonToolbar,
} from "@ionic/react";
import React, { useContext, useEffect, useState } from "react";
import ErrorAlert from "../../Errors/ErrorAlert";
import InformationBanner from "../../Banners/InformationBanner";
import Spinner from "../../Spinners/Spinner";
import { close } from "ionicons/icons";
import { updateEvent } from "../../../utilities/events/Event";
import { PersonContext } from "../../../context/PersonContext";
import RequiredInputIndicator from "../../Forms/RequiredInputIndicator";
import moment from "moment";
import { UpdateEventInput } from "../../../API";

interface _Props {
    event: Event
}

const EventWebLinksForm: React.FC<_Props> = ({event}) => {
    const user = useContext(PersonContext);

    const [isLoadingCard, setIsLoadingCard] = useState(false);
    const [isLoadingModal, setIsLoadingModal] = useState(false);
    const [cardError, setCardError] = useState("");
    const [modalError, setModalError] = useState("");

    // Main Card State
    const [eventWebLinks, setEventWebLinks] = useState<(WebLink | null)[] | null | undefined>(undefined);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [url, setUrl] = useState<string>("");
    const [title, setTitle] = useState<string>("");
    
    async function formatWebLinks(webLinks: any[]) {
        setIsLoadingCard(true);
        const sortedWebLinks = webLinks.sort((a, b) => b.createdOn.localeCompare(a.createdOn));;
        setEventWebLinks(sortedWebLinks);
        setIsLoadingCard(false);
        return sortedWebLinks;
    }

    useEffect(() => {
        if (event && event.webLinks && !eventWebLinks) {
            formatWebLinks(event.webLinks);
        }
    }, [event]);

    const handleSaveNewWebLink = async () => {
        // Check to make sure the URL is included
        setModalError("");
        if (!url || url === "") {
            setModalError("Please include a URL.");
            return;
        }

        setIsLoadingModal(true);

        // Create a new object for the new link
        const newWebLink: WebLink = {
            url: url,
            title: title,
            createdOn: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
        };

        // Concat the new link at the top of the list
        const updatedWebLinks: WebLink[] = [newWebLink];
        if (eventWebLinks && eventWebLinks.length > 0) {
            eventWebLinks.forEach(eventWebLink => {
                if (eventWebLink) updatedWebLinks.push(eventWebLink);
            });
        } 
        
        // Update the event
        if (event) {
            const eventInput: UpdateEventInput = {
                id: event.id,
                lastUpdatedBy: user.id,
                webLinks: updatedWebLinks
            };
            const updateResult = await updateEvent(eventInput);
            if (updateResult.isSuccess) {
                const updatedEvent: Event = updateResult.result;
                const updatedWebLinks = updatedEvent.webLinks;
                setEventWebLinks(updatedWebLinks);
            } else {
                setModalError("Could not save the web links to your event. A database error occurred.");
            }
        } else {
            setModalError("Could not save the web links to your event. No event found.");
        }
        setShowModal(false);
        setUrl("");
        setTitle("");
        setIsLoadingModal(false);
    }

    const handleDelete = async (index: number, webLink: WebLink) => {
        setIsLoadingCard(true);
        let updatedWebLinks: WebLink[] = [];
        if (eventWebLinks && eventWebLinks.length) {
            for (let i = 0; i < eventWebLinks.length; i++) {
                const current = eventWebLinks[i];
                if (current) {
                    if (i === index && webLink.url === current?.url) {
                        continue;
                    } else {
                        updatedWebLinks.push(current);
                    }
                }
            }
            const eventInput: UpdateEventInput = {
                id: event.id,
                lastUpdatedBy: user.id,
                webLinks: updatedWebLinks
            };
            const updateResult = await updateEvent(eventInput);
            if (updateResult.isSuccess) {
                const updatedEvent: Event = updateResult.result;
                const updatedWebLinks = updatedEvent.webLinks;
                setEventWebLinks(updatedWebLinks);
            } else {
                setCardError("Could not updated the web links for your event. A database error occurred.");
            }
        }
        setEventWebLinks(updatedWebLinks);
        setIsLoadingCard(false);
    }

    return (
        <>
            {isLoadingCard ?
                <IonRow className="ion-justify-content-center">
                    <IonCol className="ion-text-center">
                        <Spinner />
                    </IonCol>
                </IonRow>
                :
                <>
                    {cardError && <ErrorAlert width="12" error={cardError} />}
                    <IonRow>
                        <IonCol>
                            <IonButton color="tertiary" onClick={() => setShowModal(true)}>
                                Add WebLink
                            </IonButton>
                        </IonCol>
                    </IonRow>
                    {eventWebLinks && eventWebLinks.length ?
                        <>
                            <IonRow>
                                <IonCol>
                                    <IonList className="bg-white">
                                        {eventWebLinks.map((webLink, index) => (
                                            <IonItem key={index}>
                                                <IonLabel>
                                                    {webLink?.url ?
                                                        <IonRow>
                                                            <IonCol size="12">
                                                                {(webLink && webLink.title) ?
                                                                    <a href={webLink?.url} rel="noopener noreferrer"  target="_blank" >
                                                                        <h2 className="ion-text-wrap link">{webLink.title}</h2>
                                                                    </a>
                                                                    :
                                                                    <></>
                                                                } 
                                                            </IonCol>
                                                            <IonCol size="12">
                                                                <a href={webLink?.url} rel="noopener noreferrer"  target="_blank" >
                                                                    <p className="ion-text-wrap link">Full Link: {webLink && webLink.url}</p>
                                                                </a>
                                                            </IonCol>
                                                            <IonCol size="12">
                                                                <p className="description ion-text-wrap">(Last Updated: {webLink?.createdOn ? moment(webLink.createdOn).format("MM/DD/YYYY hh:mm a") : ""})</p>
                                                            </IonCol>
                                                        </IonRow>
                                                        :
                                                        <IonRow>
                                                            <p>Error - web link URL is not available.</p>
                                                        </IonRow>
                                                    }
                                                </IonLabel>
                                                <IonButton slot="end" color="danger" onClick={() => handleDelete(index, webLink as WebLink)}><IonIcon icon={close}/></IonButton>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonCol>
                            </IonRow>
                        </>
                        :
                        <p></p>
                    }
                </>
            }
            <IonModal backdropDismiss={false} isOpen={showModal} id="addS3ObjectModal">
                <IonToolbar color="light">
                    <IonTitle className="ion-text-center">
                        Add Web Link
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            fill="clear"
                            onClick={() => setShowModal(false)}
                        >
                            <p id="closeWebLinkModalBtn" className="font-weight-normal text-medium text-capitalize">
                                <IonIcon icon={close} />
                            </p>
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                <IonContent className="ion-padding">
                    {isLoadingModal ?
                        <IonRow className="ion-justify-content-center">
                            <IonCol className="ion-text-center">
                                <Spinner />
                            </IonCol>
                        </IonRow>
                        :
                        <>
                            <InformationBanner info="Choose a web link to include. Include your horse show website, link to a prize list, etc." />
                            {modalError && <ErrorAlert width="12" error={modalError} />}
                            <form>
                                <IonRow className="ion-margin">
                                    <IonCol sizeMd="12">
                                        <IonItem>
                                            <IonLabel position="floating">URL Link <RequiredInputIndicator /></IonLabel>
                                            <IonInput
                                                type="text"
                                                value={url}
                                                aria-required={true}
                                                onIonChange={e => {
                                                    setUrl(e.detail.value!)
                                                }}
                                            />
                                        </IonItem>
                                    </IonCol>
                                </IonRow>
                                <IonRow className="ion-margin">
                                    <IonCol sizeMd="12">
                                        <IonItem>
                                            <IonLabel position="floating">Title</IonLabel>
                                            <IonInput
                                                type="text"
                                                value={title}
                                                aria-required={true}
                                                onIonChange={e => {
                                                    setTitle(e.detail.value!)
                                                }}
                                            />
                                        </IonItem>
                                    </IonCol>
                                </IonRow>
                                <IonRow>
                                    <IonCol className="ion-text-center">
                                        <IonButton color="tertiary" onClick={() => handleSaveNewWebLink()}>Save</IonButton>
                                    </IonCol>
                                </IonRow>
                            </form>
                        </>
                    }
                </IonContent>
            </IonModal>
        </>
    );
};

export default EventWebLinksForm;